import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { useWorkerJob } from '../hooks/useWorkerJob.js'
import { useWorkerJobsStore } from '../stores/workerJobsStore.js'

// Une tâche suivie depuis plus longtemps est abandonnée (ex. supprimée côté serveur).
const MAX_TRACK_MS = 6 * 60 * 60 * 1000

/** Ce qu'on fait à la fin de chaque type de tâche : message + données à rafraîchir. */
const JOB_OUTCOMES = {
  'france_travail.search': {
    invalidate: [['franceTravailCandidates']],
    success: (job) => {
      const count = job.result?.cv_queued ?? 0
      return count > 0
        ? `Recherche France Travail terminée : ${count} CV en cours d'analyse. Les candidats arrivent dans le CRM au fur et à mesure.`
        : 'Recherche France Travail terminée : aucun nouveau profil avec CV.'
    },
    failure: 'La recherche France Travail a échoué',
  },
  'linkedin.search': {
    invalidate: [],
    success: (job) => {
      const count = job.result?.found ?? 0
      return count > 0
        ? `Recherche LinkedIn terminée : ${count} profil${count > 1 ? 's' : ''} trouvé${count > 1 ? 's' : ''}. Choisissez ceux à ajouter dans Prospection > LinkedIn.`
        : 'Recherche LinkedIn terminée : aucun profil trouvé.'
    },
    failure: 'La recherche LinkedIn a échoué',
  },
  'france_travail.poei': {
    invalidate: [],
    success: () => 'Demande POEI : le formulaire a été rempli sur France Travail.',
    failure: 'La demande POEI a échoué',
  },
}

function WatchedJob({ entry }) {
  const queryClient = useQueryClient()
  const untrack = useWorkerJobsStore((state) => state.untrack)
  const { job, error } = useWorkerJob(entry.id)

  useEffect(() => {
    // Tâche introuvable (404) ou suivie depuis trop longtemps : on arrête de la suivre.
    if (error?.response?.status === 404 || Date.now() - entry.trackedAt > MAX_TRACK_MS) {
      untrack(entry.id)
      return
    }
    if (!job || !['completed', 'failed'].includes(job.status)) return

    const outcome = JOB_OUTCOMES[job.kind] || { invalidate: [], success: () => 'Tâche terminée.', failure: 'La tâche a échoué' }
    if (job.status === 'completed') {
      toast.success(outcome.success(job), { duration: 8000 })
      outcome.invalidate.forEach((queryKey) => queryClient.invalidateQueries({ queryKey }))
    } else {
      toast.error(`${outcome.failure} : ${(job.error || 'erreur inconnue').split('\n')[0]}`, { duration: 8000 })
    }
    untrack(entry.id)
  }, [job?.status, error]) // eslint-disable-line react-hooks/exhaustive-deps

  return null
}

/** Monté une fois dans DashboardLayout. */
export default function WorkerJobsWatcher() {
  const jobs = useWorkerJobsStore((state) => state.jobs)
  return jobs.map((entry) => <WatchedJob key={entry.id} entry={entry} />)
}
